import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { animated, config, useSpring } from "@react-spring/web";

import OttoPeek from "../assets/otto-peek.svg";
import ScribblesPeek from "../assets/scribbles-peek.svg";
import { DSButton, DSCardWrapper } from "../molecules";

let openModalCount = 0;

/**
 * How many modals are currently open. Lets a document-level key handler (e.g.
 * Companion's Esc-Esc shortcut) tell synchronously, at keydown time, whether an
 * Escape belongs to an open modal before that modal's own listener closes it.
 */
export const getOpenModalCount = (): number => openModalCount;

// Stack of all open modals, in open order. The document keydown listener is
// per-instance and only the top of the stack reacts to Escape, so stacked
// modals don't all dismiss at once — and a non-dismissable modal on top
// blocks Escape rather than letting it close an obscured modal beneath.
const escapeStack: object[] = [];

// Easter-egg hook: web wires this up at root with its dark-themed
// `alert("BOOP!")` toast; admin / other apps leave it unset and the
// click div is omitted entirely. The design system can't import
// web's toast helper, so the handler is plumbed through context.
const MascotClickContext = createContext<(() => void) | undefined>(undefined);

export function DSMascotClickProvider({
  onMascotClick,
  children,
}: {
  onMascotClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <MascotClickContext.Provider value={onMascotClick}>
      {children}
    </MascotClickContext.Provider>
  );
}

export interface DSModalAction {
  label: string;
  variant?: "primary" | "secondary" | "cancel" | "danger";
  onClick: () => void;
  disabled?: boolean;
  dataTestId?: string;
}

export interface DSModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Action buttons in the footer. With actions: interactive modal
   *  (X button + backdrop close). Without: informational. */
  actions?: DSModalAction[];
  /** Override the inferred dismissible behaviour. */
  dismissible?: boolean;
  /** Disable backdrop click while keeping the X button. */
  disableBackdropClose?: boolean;
  /** Default: `w-[50vw] max-w-[600px]`. */
  width?: string;
  /** Extra classes for the title `<h2>` (e.g. `text-white` on a dark surface). */
  titleClassName?: string;
  /** Extra classes for the subtitle `<p>` — same purpose as `titleClassName`. */
  subtitleClassName?: string;
  dataTestId: string;
}

/**
 * Reusable modal dialog: portaled, backdrop-dismissable when
 * actions are present, animated open. Action variants include
 * `danger` for destructive confirms.
 */
export const DSModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  actions,
  dismissible,
  disableBackdropClose = false,
  width = "w-[50vw] max-w-[600px]",
  titleClassName,
  subtitleClassName,
  dataTestId,
}: DSModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const onMascotClick = useContext(MascotClickContext);

  const hasActions = actions && actions.length > 0;
  const isInteractive = dismissible ?? hasActions;

  const modalAnim = useSpring({
    config: config.stiff,
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? "scale(1)" : "scale(0.8)",
  });

  useEffect(() => {
    if (isOpen) {
      openModalCount++;
      document.body.style.overflow = "hidden";
    }

    return () => {
      if (isOpen) {
        openModalCount--;
        if (openModalCount === 0) {
          document.body.style.overflow = "";
        }
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  // Reads the latest dismissability so the keydown effect can key on `isOpen`
  // alone — the modal's stack position then only moves when it opens/closes,
  // not when a prop like `disableBackdropClose` toggles mid-open.
  const handleEscape = useEffectEvent(() => {
    if (isInteractive && !disableBackdropClose) onClose();
  });

  useEffect(() => {
    if (!isOpen) return;
    const token = {};
    escapeStack.push(token);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && escapeStack.at(-1) === token) handleEscape();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const i = escapeStack.lastIndexOf(token);
      if (i !== -1) escapeStack.splice(i, 1);
    };
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isInteractive || disableBackdropClose) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-lg bg-black/80"
      onClick={handleBackdropClick}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
    >
      <animated.div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        className={`relative ${width} max-h-[90vh] flex flex-col focus:outline-none`}
        style={modalAnim}
      >
        {onMascotClick && (
          <div
            onClick={onMascotClick}
            className="absolute z-40 w-4 h-4 cursor-pointer top-[-22px] left-[94px]"
          />
        )}
        <img
          className="absolute z-30 top-[-50px] left-[40px] w-[120px]"
          src={OttoPeek}
          alt="Otto"
        />
        <img
          className="absolute z-30 top-[-43px] right-[40px] w-[80px]"
          src={ScribblesPeek}
          alt="Scribbles"
        />

        <DSCardWrapper
          padding="p-0"
          variant="modal"
          dataTestId={`${dataTestId}-card`}
        >
          <div className="relative p-md shrink-0">
            <div className="flex flex-col gap-sm items-end">
              {isInteractive && (
                <button
                  onClick={onClose}
                  className="shrink-0 w-3 h-3 flex items-center justify-center text-primary hover:text-secondary transition-colors"
                  aria-label="Close modal"
                >
                  <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                </button>
              )}

              <h2 id="modal-title" className={`w-full ${titleClassName ?? ""}`}>
                {title}
              </h2>

              {subtitle && (
                <p className={`w-full ${subtitleClassName ?? ""}`}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          <div className="px-md pb-md overflow-y-auto min-h-0">
            <div className="text-primary">{children}</div>
          </div>

          {hasActions && (
            <div className="bg-secondary rounded-b-large p-md flex items-center justify-between gap-md shrink-0">
              <div className="flex gap-xs">
                {actions
                  .filter((action) => action.variant === "cancel")
                  .map((action, index) => (
                    <DSButton
                      key={index}
                      variant="cancel"
                      size="md"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      dataTestId={
                        action.dataTestId || `${dataTestId}-cancel-${index}`
                      }
                    >
                      {action.label}
                    </DSButton>
                  ))}
              </div>

              <div className="flex gap-xs">
                {actions
                  .filter((action) => action.variant !== "cancel")
                  .map((action, index) => (
                    <DSButton
                      key={index}
                      variant={action.variant || "primary"}
                      size="md"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      dataTestId={
                        action.dataTestId ||
                        `${dataTestId}-${action.variant || "primary"}-${index}`
                      }
                    >
                      {action.label}
                    </DSButton>
                  ))}
              </div>
            </div>
          )}
        </DSCardWrapper>
      </animated.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

DSModal.displayName = "DSModal";
